import React, { useState, lazy, Suspense } from "react";
import { motion } from "framer-motion";
import { AuthAPI } from "../api/AuthAPI";

/* ================= LAZY MAP ================= */
const MapFrame = lazy(() =>
  Promise.resolve({
    default: () => (
      <iframe
        title="Confidays Location"
        className="w-full h-[400px] rounded-2xl border border-gray-200 shadow-sm"
        loading="lazy"
        src="https://maps.google.com/maps?q=Pent%20House%2C%20Plot%20No.%20178%2C%20KPHB%2C%20Hyderabad%20-%20500072&t=m&z=12&output=embed"
      />
    ),
  }),
);

export default function ContactUs() {
  /* ================= LEFT FORM ================= */
  const [leftForm, setLeftForm] = useState({
    name: "",
    email: "",
    phone: "",
    message: "",
  });

  /* ================= RIGHT FORM ================= */
  const [rightForm, setRightForm] = useState({
    name: "",
    email: "",
    phone: "",
    quantity: "",
  });

  const [loading, setLoading] = useState(false);
  const [successMsg, setSuccessMsg] = useState("");
  const [errorMsg, setErrorMsg] = useState("");

  /* ================= VALIDATION ================= */
  const isValidEmail = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

  const isValidPhone = (phone) => /^[0-9]{10}$/.test(phone);

  /* ================= HANDLERS ================= */
  const handleLeftChange = (e) => {
    const { name, value } = e.target;

    if (name === "phone") {
      if (!/^\d*$/.test(value)) return;
      if (value.length > 10) return;
    }

    setLeftForm({ ...leftForm, [name]: value });
  };

  const handleRightChange = (e) => {
    const { name, value } = e.target;

    if (name === "phone") {
      if (!/^\d*$/.test(value)) return;
      if (value.length > 10) return;
    }

    setRightForm({ ...rightForm, [name]: value });
  };

  /* ================= LEFT SUBMIT ================= */
  const submitLeftForm = async (e) => {
    e.preventDefault();

    setSuccessMsg("");
    setErrorMsg("");

    if (!isValidEmail(leftForm.email)) {
      setErrorMsg("Please enter a valid email address");
      return;
    }

    if (!isValidPhone(leftForm.phone)) {
      setErrorMsg("Phone number must be exactly 10 digits");
      return;
    }

    setLoading(true);

    try {
      await AuthAPI.createcontantus({
        name: leftForm.name,
        email: leftForm.email,
        phone: leftForm.phone,
        message: leftForm.message,
      });

      setSuccessMsg("Your inquiry has been submitted successfully.");

      setLeftForm({
        name: "",
        email: "",
        phone: "",
        message: "",
      });
    } catch {
      setErrorMsg("Failed to submit your inquiry.");
    } finally {
      setLoading(false);
    }
  };

  /* ================= RIGHT SUBMIT ================= */
  const submitRightForm = async (e) => {
    e.preventDefault();

    setSuccessMsg("");
    setErrorMsg("");

    if (!isValidEmail(rightForm.email)) {
      setErrorMsg("Please enter a valid email address");
      return;
    }

    if (!isValidPhone(rightForm.phone)) {
      setErrorMsg("Phone number must be exactly 10 digits");
      return;
    }

    setLoading(true);

    try {
      await AuthAPI.createcontantus({
        name: rightForm.name,
        email: rightForm.email,
        phone: rightForm.phone,
        message: rightForm.quantity,
      });

      setSuccessMsg("Distributor request submitted successfully.");

      setRightForm({
        name: "",
        email: "",
        phone: "",
        quantity: "",
      });
    } catch {
      setErrorMsg("Failed to submit distributor request.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="text-[#555] font-poppins overflow-hidden mt-6 md:mt-0">
      {/* ================= HEADER ================= */}
      <section className="bg-gradient-to-b from-[#6A1B9A] to-[#9C27B0] py-14 text-center text-white">
        <motion.h1
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="text-3xl sm:text-4xl md:text-5xl font-bold"
        >
          Contact Us
        </motion.h1>
      </section>

      {/* ================= OFFICE SECTION ================= */}
      <section
        className="py-16 md:py-20"
        style={{
          backgroundImage: "url('/Map.jpg')",
          backgroundRepeat: "no-repeat",
          backgroundPosition: "center top",
          backgroundSize: "contain",
        }}
      >
        <div className="max-w-7xl mx-auto px-5 sm:px-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-10 md:gap-16 items-start">
            {/* LEFT */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.4 }}
              viewport={{ once: true }}
            >
              <h2 className="text-[#0b1fa3] font-semibold text-2xl">
                Our Offices
              </h2>
            </motion.div>

            {/* CENTER */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              viewport={{ once: true }}
            >
              <h3 className="text-[#0b1fa3] font-semibold text-xl mb-3">
                Hyderabad
              </h3>

              <p className="text-sm leading-7">
                <span className="font-semibold">Confidays</span>
                <br />
                Pent House, Plot No. 178, KPHB,
                <br />
                Kukatpally, Medchal-Malkajgiri,
                <br />
                Hyderabad – 500072
              </p>
            </motion.div>

            {/* RIGHT */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.5 }}
              viewport={{ once: true }}
              className="space-y-5"
            >
              <div>
                <h3 className="text-[#0b1fa3] font-semibold text-xl mb-3">
                  Phone Number
                </h3>

                <a
                  href="tel:+919704359405"
                  className="text-sm hover:underline transition-all duration-300"
                >
                  +91-9704359405
                </a>
              </div>

              <div>
                <h3 className="text-[#0b1fa3] font-semibold text-xl mb-3">
                  Email Address
                </h3>

                <a
                  href="mailto:info@confidays.com"
                  className="text-sm hover:underline transition-all duration-300"
                >
                  info@confidays.com
                </a>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ================= FORM SECTION ================= */}
      <section className="max-w-7xl mx-auto px-5 sm:px-6 py-16 md:py-20">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-start">
          {/* MAP */}
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            viewport={{ once: true }}
          >
            <Suspense
              fallback={
                <div className="w-full h-[400px] rounded-2xl bg-gray-100 animate-pulse" />
              }
            >
              <MapFrame />
            </Suspense>
          </motion.div>

          {/* DISTRIBUTOR FORM */}
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            viewport={{ once: true }}
            className="bg-white rounded-2xl shadow-sm p-6 md:p-8 border border-gray-100"
          >
            <h2 className="text-[#0b1fa3] font-semibold text-2xl mb-8">
              For Distributors
            </h2>

            <form className="space-y-5" onSubmit={submitRightForm}>
              <Input
                label="Name"
                name="name"
                value={rightForm.name}
                onChange={handleRightChange}
              />

              <Input
                label="Email"
                name="email"
                type="email"
                value={rightForm.email}
                onChange={handleRightChange}
              />

              <Input
                label="Phone Number"
                name="phone"
                value={rightForm.phone}
                onChange={handleRightChange}
                placeholder="10 digit mobile number"
              />

              <div>
                <label className="block text-sm font-semibold mb-2">
                  Quantity / Statement
                </label>

                <textarea
                  rows={4}
                  name="quantity"
                  value={rightForm.quantity}
                  onChange={handleRightChange}
                  placeholder="Enter quantity or statement"
                  className="w-full border border-gray-300 rounded-lg px-4 py-3 text-sm outline-none focus:border-[#6A1B9A] transition-all duration-300 resize-none"
                />
              </div>

              <button
                type="submit"
                disabled={loading}
                className="bg-brandPurple text-white px-8 py-3 rounded-lg text-sm transition-all duration-300 hover:scale-[1.02] active:scale-[0.98]"
              >
                {loading ? "Submitting..." : "Submit"}
              </button>
            </form>

            {successMsg && (
              <p className="text-green-600 text-sm mt-5">{successMsg}</p>
            )}

            {errorMsg && (
              <p className="text-red-600 text-sm mt-5">{errorMsg}</p>
            )}
          </motion.div>
        </div>
      </section>
    </div>
  );
}

/* ================= INPUT COMPONENT ================= */
function Input({ label, name, value, onChange, placeholder, type = "text" }) {
  return (
    <div>
      <label className="block text-sm font-semibold mb-2">
        {label} <span className="text-red-500">*</span>
      </label>

      <input
        type={type}
        name={name}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        required
        className="w-full border border-gray-300 rounded-lg px-4 py-3 text-sm outline-none focus:border-[#6A1B9A] transition-all duration-300"
      />
    </div>
  );
}


























// import React, { useState, lazy, Suspense } from "react";
// import { motion } from "framer-motion";
// import { AuthAPI } from "../api/AuthAPI";

// import { MapPin } from "lucide-react";
// import { Navigation } from "lucide-react";

// const MapFrame = lazy(() =>
//   Promise.resolve({
//     default: () => (
//       <div className="relative">
//         <iframe
//           title="Confidays Location"
//           className="w-full h-[600px] rounded-2xl border border-gray-200 shadow-sm"
//           loading="lazy"
//           src="https://maps.google.com/maps?q=Confidays%2C%20Pent%20House%2C%20Plot%20No.%20178%2C%20KPHB%2C%20Kukatpally%2C%20Medchal-Malkajgiri%2C%20Hyderabad%20-%20500072&t=m&z=16&output=embed"
//         />

//         {/* ===== Floating Get Directions button ===== */}
   
//           <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-full flex flex-col items-center pointer-events-none">
//           <MapPin
//             size={44}
//             className="text-[#6A1B9A] drop-shadow-lg animate-bounce"
//             fill="#9C27B0"
//           />
//           <span className="mt-1 bg-white text-[#6A1B9A] text-xs font-semibold px-2 py-1 rounded-full shadow-md whitespace-nowrap">
//             Confidays, KPHB
//           </span>
//         </div>
//       </div>
//     ),
//   }),
// );

// export default function ContactUs() {
//   /* ================= LEFT FORM ================= */
//   const [leftForm, setLeftForm] = useState({
//     name: "",
//     email: "",
//     phone: "",
//     message: "",
//   });

//   /* ================= RIGHT FORM ================= */
//   const [rightForm, setRightForm] = useState({
//     name: "",
//     email: "",
//     phone: "",
//     quantity: "",
//   });

//   const [loading, setLoading] = useState(false);
//   const [successMsg, setSuccessMsg] = useState("");
//   const [errorMsg, setErrorMsg] = useState("");

//   /* ================= VALIDATION ================= */
//   const isValidEmail = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

//   const isValidPhone = (phone) => /^[0-9]{10}$/.test(phone);

//   /* ================= HANDLERS ================= */
//   const handleLeftChange = (e) => {
//     const { name, value } = e.target;

//     if (name === "phone") {
//       if (!/^\d*$/.test(value)) return;
//       if (value.length > 10) return;
//     }

//     setLeftForm({ ...leftForm, [name]: value });
//   };

//   const handleRightChange = (e) => {
//     const { name, value } = e.target;

//     if (name === "phone") {
//       if (!/^\d*$/.test(value)) return;
//       if (value.length > 10) return;
//     }

//     setRightForm({ ...rightForm, [name]: value });
//   };

//   /* ================= LEFT SUBMIT ================= */
//   const submitLeftForm = async (e) => {
//     e.preventDefault();

//     setSuccessMsg("");
//     setErrorMsg("");

//     if (!isValidEmail(leftForm.email)) {
//       setErrorMsg("Please enter a valid email address");
//       return;
//     }

//     if (!isValidPhone(leftForm.phone)) {
//       setErrorMsg("Phone number must be exactly 10 digits");
//       return;
//     }

//     setLoading(true);

//     try {
//       await AuthAPI.createcontantus({
//         name: leftForm.name,
//         email: leftForm.email,
//         phone: leftForm.phone,
//         message: leftForm.message,
//       });

//       setSuccessMsg("Your inquiry has been submitted successfully.");

//       setLeftForm({
//         name: "",
//         email: "",
//         phone: "",
//         message: "",
//       });
//     } catch {
//       setErrorMsg("Failed to submit your inquiry.");
//     } finally {
//       setLoading(false);
//     }
//   };

//   /* ================= RIGHT SUBMIT ================= */
//   const submitRightForm = async (e) => {
//     e.preventDefault();

//     setSuccessMsg("");
//     setErrorMsg("");

//     if (!isValidEmail(rightForm.email)) {
//       setErrorMsg("Please enter a valid email address");
//       return;
//     }

//     if (!isValidPhone(rightForm.phone)) {
//       setErrorMsg("Phone number must be exactly 10 digits");
//       return;
//     }

//     setLoading(true);

//     try {
//       await AuthAPI.createcontantus({
//         name: rightForm.name,
//         email: rightForm.email,
//         phone: rightForm.phone,
//         message: rightForm.quantity,
//       });

//       setSuccessMsg("Distributor request submitted successfully.");

//       setRightForm({
//         name: "",
//         email: "",
//         phone: "",
//         quantity: "",
//       });
//     } catch {
//       setErrorMsg("Failed to submit distributor request.");
//     } finally {
//       setLoading(false);
//     }
//   };

//   return (
//     <div className="text-[#555] font-poppins overflow-hidden mt-6 md:mt-0">
//       {/* ================= HEADER ================= */}
//       <section className="bg-gradient-to-b from-[#6A1B9A] to-[#9C27B0] py-14 text-center text-white">
//         <motion.h1
//           initial={{ opacity: 0, y: 20 }}
//           animate={{ opacity: 1, y: 0 }}
//           transition={{ duration: 0.5 }}
//           className="text-3xl sm:text-4xl md:text-5xl font-bold"
//         >
//           Contact Us
//         </motion.h1>
//       </section>

//       {/* ================= OFFICE SECTION ================= */}
//       <section
//         className="py-16 md:py-20"
//         style={{
//           backgroundImage: "url('/Map.jpg')",
//           backgroundRepeat: "no-repeat",
//           backgroundPosition: "center top",
//           backgroundSize: "contain",
//         }}
//       >
//         <div className="max-w-7xl mx-auto px-5 sm:px-6">
//           <div className="grid grid-cols-1 md:grid-cols-3 gap-10 md:gap-16 items-start">
//             {/* LEFT */}
//             <motion.div
//               initial={{ opacity: 0, x: -30 }}
//               whileInView={{ opacity: 1, x: 0 }}
//               transition={{ duration: 0.4 }}
//               viewport={{ once: true }}
//             >
//               <h2 className="text-[#0b1fa3] font-semibold text-2xl">
//                 Our Offices
//               </h2>
//             </motion.div>

//             {/* CENTER */}
//             <motion.div
//               initial={{ opacity: 0, y: 30 }}
//               whileInView={{ opacity: 1, y: 0 }}
//               transition={{ duration: 0.5 }}
//               viewport={{ once: true }}
//             >
//               <h3 className="text-[#0b1fa3] font-semibold text-xl mb-3">
//                 Hyderabad
//               </h3>

//               <p className="text-sm leading-7">
//                 <span className="font-semibold">Confidays</span>
//                 <br />
//                 Pent House, Plot No. 178, KPHB,
//                 <br />
//                 Kukatpally, Medchal-Malkajgiri,
//                 <br />
//                 Hyderabad – 500072
//               </p>
//             </motion.div>

//             {/* RIGHT */}
//             <motion.div
//               initial={{ opacity: 0, x: 30 }}
//               whileInView={{ opacity: 1, x: 0 }}
//               transition={{ duration: 0.5 }}
//               viewport={{ once: true }}
//               className="space-y-5"
//             >
//               <div>
//                 <h3 className="text-[#0b1fa3] font-semibold text-xl mb-3">
//                   Phone Number
//                 </h3>

//                 <a
//                   href="tel:+919704359405"
//                   className="text-sm hover:underline transition-all duration-300"
//                 >
//                   +91-9704359405
//                 </a>
//               </div>

//               <div>
//                 <h3 className="text-[#0b1fa3] font-semibold text-xl mb-3">
//                   Email Address
//                 </h3>

//                 <a
//                   href="mailto:info@confidays.com"
//                   className="text-sm hover:underline transition-all duration-300"
//                 >
//                   info@confidays.com
//                 </a>
//               </div>
//             </motion.div>
//           </div>
//         </div>
//       </section>

//       {/* ================= FORM SECTION ================= */}
//       <section className="max-w-7xl mx-auto px-5 sm:px-6 py-16 md:py-20">
//         <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-start">
//           {/* MAP */}
//           <motion.div
//             initial={{ opacity: 0, y: 40 }}
//             whileInView={{ opacity: 1, y: 0 }}
//             transition={{ duration: 0.5 }}
//             viewport={{ once: true }}
//           >
//             <Suspense
//               fallback={
//                 <div className="w-full h-[400px] rounded-2xl bg-gray-100 animate-pulse" />
//               }
//             >
//               <MapFrame />
//             </Suspense>
//           </motion.div>

//           {/* DISTRIBUTOR FORM */}
//           <motion.div
//             initial={{ opacity: 0, y: 40 }}
//             whileInView={{ opacity: 1, y: 0 }}
//             transition={{ duration: 0.5 }}
//             viewport={{ once: true }}
//             className="bg-white rounded-2xl shadow-sm p-6 md:p-8 border border-gray-100"
//           >
//             <h2 className="text-[#0b1fa3] font-semibold text-2xl mb-8">
//               For Distributors
//             </h2>

//             <form className="space-y-5" onSubmit={submitRightForm}>
//               <Input
//                 label="Name"
//                 name="name"
//                 value={rightForm.name}
//                 onChange={handleRightChange}
//               />

//               <Input
//                 label="Email"
//                 name="email"
//                 type="email"
//                 value={rightForm.email}
//                 onChange={handleRightChange}
//               />

//               <Input
//                 label="Phone Number"
//                 name="phone"
//                 value={rightForm.phone}
//                 onChange={handleRightChange}
//                 placeholder="10 digit mobile number"
//               />

//               <div>
//                 <label className="block text-sm font-semibold mb-2">
//                   Quantity / Statement
//                 </label>

//                 <textarea
//                   rows={4}
//                   name="quantity"
//                   value={rightForm.quantity}
//                   onChange={handleRightChange}
//                   placeholder="Enter quantity or statement"
//                   className="w-full border border-gray-300 rounded-lg px-4 py-3 text-sm outline-none focus:border-[#6A1B9A] transition-all duration-300 resize-none"
//                 />
//               </div>

//               <button
//                 type="submit"
//                 disabled={loading}
//                 className="bg-brandPurple text-white px-8 py-3 rounded-lg text-sm transition-all duration-300 hover:scale-[1.02] active:scale-[0.98]"
//               >
//                 {loading ? "Submitting..." : "Submit"}
//               </button>
//             </form>

//             {successMsg && (
//               <p className="text-green-600 text-sm mt-5">{successMsg}</p>
//             )}

//             {errorMsg && (
//               <p className="text-red-600 text-sm mt-5">{errorMsg}</p>
//             )}
//           </motion.div>
//         </div>
//       </section>
//     </div>
//   );
// }

// /* ================= INPUT COMPONENT ================= */
// function Input({ label, name, value, onChange, placeholder, type = "text" }) {
//   return (
//     <div>
//       <label className="block text-sm font-semibold mb-2">
//         {label} <span className="text-red-500">*</span>
//       </label>

//       <input
//         type={type}
//         name={name}
//         value={value}
//         onChange={onChange}
//         placeholder={placeholder}
//         required
//         className="w-full border border-gray-300 rounded-lg px-4 py-3 text-sm outline-none focus:border-[#6A1B9A] transition-all duration-300"
//       />
//     </div>
//   );
// }
